'use client';

import React, { useCallback, useState } from 'react';
import { useDropzone, Accept, FileRejection } from 'react-dropzone';
import { UploadIcon, FileIcon, Cross2Icon } from '@radix-ui/react-icons';

import { Box, BoxProps } from '../box';
import { Text } from '../text';
import { Icon } from '../icon';
import { Stack } from '../stack';

export interface FileDropzoneProps extends Omit<BoxProps, 'onDrop' | 'onError'> {
  /** Callback when files are dropped */
  onDrop: (files: File[]) => void;
  /** Accepted file types */
  accept?: Accept;
  /** Maximum file size in bytes */
  maxSize?: number;
  /** Maximum number of files */
  maxFiles?: number;
  /** Minimum number of files */
  minFiles?: number;
  /** Whether multiple files can be selected */
  multiple?: boolean;
  /** Whether the dropzone is disabled */
  disabled?: boolean;
  /** Custom content for the dropzone */
  children?: React.ReactNode;
  /** Show file preview after selection */
  showPreview?: boolean;
  /** Error handler for rejected files */
  onError?: (rejections: FileRejection[]) => void;
}

/**
 * FileDropzone component - drag and drop file upload area
 * Built on react-dropzone with Chakra UI styling
 */
export const FileDropzone: React.FC<FileDropzoneProps> = ({
  onDrop,
  accept,
  maxSize = 10 * 1024 * 1024, // 10MB default
  maxFiles = 10,
  minFiles: _minFiles = 0,
  multiple = true,
  disabled = false,
  children,
  showPreview = true,
  onError,
  ...boxProps
}) => {
  const [files, setFiles] = useState<File[]>([]);
  const [errors, setErrors] = useState<string[]>([]);

  const handleDrop = useCallback(
    (acceptedFiles: File[], rejectedFiles: FileRejection[]) => {
      setErrors([]);

      if (rejectedFiles.length > 0) {
        const errorMessages = rejectedFiles
          .map((rejection) => {
            const fileName = rejection.file.name;
            const errors = rejection.errors.map((e) => {
              switch (e.code) {
                case 'file-too-large':
                  return `${fileName}: File is larger than ${formatFileSize(maxSize)}`;
                case 'file-invalid-type':
                  return `${fileName}: File type not accepted`;
                case 'too-many-files':
                  return `Too many files. Maximum ${maxFiles} allowed`;
                default:
                  return `${fileName}: ${e.message}`;
              }
            });
            return errors;
          })
          .flat();

        setErrors(errorMessages);
        onError?.(rejectedFiles);
      }

      if (acceptedFiles.length > 0) {
        setFiles(acceptedFiles);
        onDrop(acceptedFiles);
      }
    },
    [maxSize, maxFiles, onDrop, onError]
  );

  const { getRootProps, getInputProps, isDragActive, isDragAccept, isDragReject } = useDropzone({
    onDrop: handleDrop,
    accept,
    maxSize,
    maxFiles,
    multiple,
    disabled,
    // HTML drag event handlers (required by DropzoneOptions type but handled internally)
    onDragEnter: undefined,
    onDragLeave: undefined,
    onDragOver: undefined,
  });

  const removeFile = (index: number) => {
    const newFiles = [...files];
    newFiles.splice(index, 1);
    setFiles(newFiles);
  };

  const handleRemoveFile = (index: number) => () => {
    removeFile(index);
  };

  const formatFileSize = (bytes: number): string => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const getAcceptText = (): string => {
    if (!accept) return 'All file types accepted';

    const types = Object.keys(accept);
    if (types.length === 1 && types[0] === 'image/*') return 'Images only';
    if (types.length === 1 && types[0] === 'application/pdf') return 'PDF files only';

    const extensions = Object.values(accept).flat();
    return `Accepted: ${extensions.join(', ')}`;
  };

  const borderColor = isDragReject
    ? 'border.error'
    : isDragAccept
      ? 'border.success'
      : isDragActive
        ? 'border.primary'
        : 'border.muted';

  const backgroundColor = isDragReject
    ? 'bg.error.subtle'
    : isDragAccept
      ? 'bg.success.subtle'
      : isDragActive
        ? 'bg.primary.subtle'
        : disabled
          ? 'bg.muted'
          : 'bg.surface';

  return (
    <Box {...boxProps}>
      <Box
        {...getRootProps()}
        padding="24px"
        border="2px dashed"
        borderColor={borderColor}
        backgroundColor={backgroundColor}
        borderRadius="lg"
        textAlign="center"
        cursor={disabled ? 'not-allowed' : 'pointer'}
        transition="all 0.2s"
        opacity={disabled ? 0.6 : 1}
        _hover={
          !disabled
            ? {
                borderColor: 'border.default',
                backgroundColor: 'bg.muted',
              }
            : undefined
        }
      >
        <input {...(getInputProps() as React.InputHTMLAttributes<HTMLInputElement>)} />

        {children || (
          <Stack gap={3} alignItems="center">
            <Icon size="3xl" color={isDragActive ? 'fg.primary' : 'fg.muted'}>
              <UploadIcon />
            </Icon>

            <Box>
              <Text fontWeight="medium" marginBottom={1}>
                {isDragActive ? 'Drop files here' : 'Drag & drop files here'}
              </Text>
              <Text fontSize="sm" color="fg.muted">
                or click to select files
              </Text>
            </Box>

            <Stack gap={1} alignItems="center">
              <Text fontSize="xs" color="fg.muted">
                {getAcceptText()}
              </Text>
              <Text fontSize="xs" color="fg.muted">
                Max size: {formatFileSize(maxSize)} • Max files: {maxFiles}
              </Text>
            </Stack>
          </Stack>
        )}
      </Box>

      {/* Error messages */}
      {errors.length > 0 && (
        <Box marginTop={3}>
          {errors.map((error, index) => (
            <Box
              key={index}
              padding="8px"
              backgroundColor="bg.error.subtle"
              borderRadius="md"
              marginBottom={1}
            >
              <Text fontSize="sm" color="fg.error">
                {error}
              </Text>
            </Box>
          ))}
        </Box>
      )}

      {/* File preview */}
      {showPreview && files.length > 0 && (
        <Box marginTop={4}>
          <Text fontSize="sm" fontWeight="medium" marginBottom={2}>
            Selected files ({files.length})
          </Text>
          <Stack gap={2}>
            {files.map((file, index) => (
              <Box
                key={index}
                display="flex"
                alignItems="center"
                justifyContent="space-between"
                padding="8px"
                backgroundColor="bg.muted"
                borderRadius="md"
              >
                <Box display="flex" alignItems="center" gap={2} flex={1}>
                  <Icon color="fg.muted">
                    <FileIcon />
                  </Icon>
                  <Box flex={1}>
                    <Text fontSize="sm" lineClamp={1}>
                      {file.name}
                    </Text>
                    <Text fontSize="xs" color="fg.muted">
                      {formatFileSize(file.size)}
                    </Text>
                  </Box>
                </Box>

                <Icon
                  color="fg.muted"
                  cursor="pointer"
                  onClick={handleRemoveFile(index)}
                  _hover={{ color: 'fg.error' }}
                >
                  <Cross2Icon />
                </Icon>
              </Box>
            ))}
          </Stack>
        </Box>
      )}
    </Box>
  );
};

export default FileDropzone;
